import { makeAutoObservable } from "mobx";
import { newUuid } from "shared/newUuid";
import type RootStore from "stores/RootStore";
import { sameId, type DataMapperSelectionQuery } from "./dataMapper.types";

/**
 * Per-tab storage key for the selection. sessionStorage, not localStorage: the
 * selection is a TAB's (two tabs on two clients' profiles must not overwrite
 * each other — the reason the standalone tool's server kept one per tab), and it
 * should survive a reload of that tab.
 */
export const DATA_MAPPER_SELECTION_KEY = "hq.dataMapper.selection";

interface StoredSelection {
  /** Whose selection this is: signing out and in as someone else in the same tab must not inherit it. */
  ownerId: string | null;
  clientId: string | null;
  profileId: string | null;
  /** The editors' selection inside the open profile (ids only). */
  schemaId: string | null;
  mappingConfigId: string | null;
  mappingId: string | null;
}

/**
 * Per-tab storage key for the tab's id: the data plane keys each tab's open
 * source by `<userId>|<tabId>` (plan §0), so every data-plane call names it.
 */
export const DATA_MAPPER_TAB_ID_KEY = "hq.dataMapper.tabId";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;


/**
 * This tab's id: read from sessionStorage (so a reload keeps the tab's open
 * source), else minted and stored. sessionStorage is per tab, so two tabs get
 * two ids. shortcut: a browser's "Duplicate tab" copies sessionStorage, so a
 * duplicated tab shares its original's id (and its open source) until one of
 * them connects again; the API keeps one source per id, so the later connect wins.
 */
export const readOrMintTabId = (): string => {
  try {
    const stored = globalThis.sessionStorage?.getItem(DATA_MAPPER_TAB_ID_KEY);
    if (stored && UUID_RE.test(stored)) return stored;
  } catch {
    // Storage disabled: fall through to an id for this page's life.
  }
  const id = newUuid();
  try {
    globalThis.sessionStorage?.setItem(DATA_MAPPER_TAB_ID_KEY, id);
  } catch {
    // Storage full or disabled: the id still holds for this page's life.
  }
  return id;
};

const EMPTY: StoredSelection = {
  ownerId: null,
  clientId: null,
  profileId: null,
  schemaId: null,
  mappingConfigId: null,
  mappingId: null,
};

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

const readStored = (): StoredSelection => {
  try {
    const raw = globalThis.sessionStorage?.getItem(DATA_MAPPER_SELECTION_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<StoredSelection>;
    const clientId = typeof parsed.clientId === "string" && parsed.clientId ? parsed.clientId : null;
    // A profile without its client is not a selection (the profile implies the client).
    const profileId =
      clientId && typeof parsed.profileId === "string" && parsed.profileId ? parsed.profileId : null;
    const ownerId = str(parsed.ownerId);
    // The editors' ids belong to the profile: none without one.
    return {
      ownerId,
      clientId,
      profileId,
      schemaId: profileId ? str(parsed.schemaId) : null,
      mappingConfigId: profileId ? str(parsed.mappingConfigId) : null,
      mappingId: profileId ? str(parsed.mappingId) : null,
    };
  } catch {
    return { ...EMPTY };
  }
};

/**
 * THE Data Mapper selection: which HQ client and which of its profiles this tab
 * is working in (plan/2026-10-09_data-mapper-into-hq.md §2.4 — the server is
 * stateless, so the browser holds the selection and every call names it).
 *
 * IDS ONLY, NEVER ENTITIES. The profile and client records stay in their
 * ServiceStores (`data-mapper-profiles`, `clients`); a screen that wants a name
 * looks it up there by these ids. Holding a profile object here would go stale
 * the moment someone renames it, and would break the house rule that ViewStores
 * never hold API entities.
 *
 * A plain class with makeAutoObservable (route ViewStores do not extend the
 * library ViewStore). Registered as `dataMapperViewStore`.
 */
class DataMapperViewStore {
  rootStore: RootStore;

  selectedClientId: string | null = null;
  selectedProfileId: string | null = null;
  /** The signed-in user who made the selection (an id, like the other two). */
  selectionOwnerId: string | null = null;
  /** This browser tab's id, sent on every data-plane call (see readOrMintTabId). */
  readonly tabId: string;

  /** Inside the open profile: the target schema the Schema and Map screens work on. */
  selectedSchemaId: string | null = null;
  /** Inside the open profile: the mapping configuration the Map and Transform screens edit. */
  selectedMappingConfigId: string | null = null;
  /** Inside that config: the field mapping whose transform chain is open (a saved mapping's id). */
  selectedMappingId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const stored = readStored();
    this.selectedClientId = stored.clientId;
    this.selectedProfileId = stored.profileId;
    this.selectionOwnerId = stored.ownerId;
    this.selectedSchemaId = stored.schemaId;
    this.selectedMappingConfigId = stored.mappingConfigId;
    this.selectedMappingId = stored.mappingId;
    this.tabId = readOrMintTabId();
    makeAutoObservable(this, { rootStore: false });
  }

  get hasClient(): boolean {
    return this.selectedClientId !== null;
  }

  get hasProfile(): boolean {
    return this.selectedProfileId !== null;
  }

  /**
   * What a scoped call sends (`?clientId&profileId`). Empty when nothing is
   * selected — which the API reads as "global rows only", so callers that need
   * a profile check `hasProfile` first rather than send this empty.
   */
  get selectionQuery(): DataMapperSelectionQuery {
    const query: DataMapperSelectionQuery = {};
    if (this.selectedClientId) query.clientId = this.selectedClientId;
    if (this.selectedProfileId) query.profileId = this.selectedProfileId;
    return query;
  }

  /**
   * What every data-plane call sends: the tab's id and the selection. The API
   * resolves the selection and refuses to read a source opened under another
   * one (`selection-mismatch`).
   */
  get dataPlaneQuery(): DataMapperSelectionQuery & { tabId: string } {
    return { tabId: this.tabId, ...this.selectionQuery };
  }

  /**
   * Bind the selection to the signed-in user. Called when a Data Mapper route
   * loads: a selection someone else made in this tab (signed out, then in as
   * another person) is dropped rather than resumed (Codex r1).
   */
  bindToUser(userId: string | number | null | undefined) {
    if (userId == null || userId === "") return;
    const id = String(userId);
    if (this.selectionOwnerId !== null && this.selectionOwnerId !== id) {
      this.selectedClientId = null;
      this.selectedProfileId = null;
      this.clearEditorSelection();
    }
    this.selectionOwnerId = id;
    this.persist();
  }

  /**
   * Choose a client. Changing the client drops the profile: a profile belongs to
   * exactly one client, and keeping it would name a selection the API refuses
   * (`profile-not-in-client`). Re-choosing the same client keeps it.
   */
  selectClient(clientId: string | null) {
    const next = clientId || null;
    if (!sameId(next, this.selectedClientId)) {
      this.selectedProfileId = null;
      this.clearEditorSelection();
    }
    this.selectedClientId = next;
    this.persist();
  }

  /** Open a profile: the client is set with it, since the profile implies it. */
  selectProfile(clientId: string, profileId: string) {
    // Another profile's schema / config / mapping ids mean nothing in this one.
    if (!sameId(profileId, this.selectedProfileId)) this.clearEditorSelection();
    this.selectedClientId = clientId;
    this.selectedProfileId = profileId;
    this.persist();
  }

  /** Close the open profile, keeping the client. */
  clearProfile() {
    this.selectedProfileId = null;
    this.clearEditorSelection();
    this.persist();
  }

  /** Choose the target schema. A different schema closes the config and mapping chosen for the old one. */
  selectSchema(schemaId: string | null) {
    const next = schemaId || null;
    if (!sameId(next, this.selectedSchemaId)) {
      this.selectedMappingConfigId = null;
      this.selectedMappingId = null;
    }
    this.selectedSchemaId = next;
    this.persist();
  }

  /** Choose the mapping configuration. A different config closes the mapping opened in the old one. */
  selectMappingConfig(configId: string | null) {
    const next = configId || null;
    if (!sameId(next, this.selectedMappingConfigId)) this.selectedMappingId = null;
    this.selectedMappingConfigId = next;
    this.persist();
  }

  /** Open one field mapping's transform chain (a SAVED mapping's id; an unsaved one has none yet). */
  selectMapping(mappingId: string | null) {
    this.selectedMappingId = mappingId || null;
    this.persist();
  }

  /**
   * Adopt the ids a profile's saved UI state names, for whichever of them this
   * tab has not chosen yet (the profile was just opened). Never overrides a
   * choice made in this tab.
   */
  adoptSavedSelection(saved: { schemaId?: string | null; mappingConfigId?: string | null; mappingId?: string | null }) {
    if (!this.selectedProfileId) return;
    if (!this.selectedSchemaId && saved.schemaId) this.selectedSchemaId = saved.schemaId;
    if (!this.selectedMappingConfigId && saved.mappingConfigId) {
      this.selectedMappingConfigId = saved.mappingConfigId;
      if (!this.selectedMappingId && saved.mappingId) this.selectedMappingId = saved.mappingId;
    }
    this.persist();
  }

  private clearEditorSelection() {
    this.selectedSchemaId = null;
    this.selectedMappingConfigId = null;
    this.selectedMappingId = null;
  }

  /** The profile was deleted (here or elsewhere): drop it if it is the open one. */
  forgetProfile(profileId: string) {
    if (sameId(profileId, this.selectedProfileId)) this.clearProfile();
  }

  /** The schema was deleted (here or elsewhere): drop it if it is the chosen one. */
  forgetSchema(schemaId: string) {
    if (sameId(schemaId, this.selectedSchemaId)) this.selectSchema(null);
  }

  /** The mapping configuration was deleted, or is not visible to this selection: drop it if chosen. */
  forgetMappingConfig(configId: string) {
    if (sameId(configId, this.selectedMappingConfigId)) this.selectMappingConfig(null);
  }

  /**
   * Called by RootStore.purgeUserData on sign-out: the selection was the
   * previous person's, in memory and in this tab's storage. `bindToUser`
   * already drops it on the next person's first Data Mapper visit; this drops
   * it at sign-out, before anyone else is signed in. The tab's id stays — it
   * names the tab, not the person.
   */
  reset() {
    this.selectedClientId = null;
    this.selectedProfileId = null;
    this.selectionOwnerId = null;
    this.clearEditorSelection();
    this.persist();
  }

  isSelectedProfile(profileId: string): boolean {
    return sameId(profileId, this.selectedProfileId);
  }

  private persist() {
    try {
      globalThis.sessionStorage?.setItem(
        DATA_MAPPER_SELECTION_KEY,
        JSON.stringify({
          ownerId: this.selectionOwnerId,
          clientId: this.selectedClientId,
          profileId: this.selectedProfileId,
          schemaId: this.selectedSchemaId,
          mappingConfigId: this.selectedMappingConfigId,
          mappingId: this.selectedMappingId,
        })
      );
    } catch {
      // Storage full or disabled: the selection still holds for this page's life.
    }
  }
}

export default DataMapperViewStore;
